export type MarketPort = {
  request<T>(path: string, body?: unknown): Promise<T>;
  watch?(onChange: () => void, onState: (state: "live" | "reconnecting") => void): () => void;
};
export type MarketLocaleText = { ru: string; en: string };
export type MarketTab = "swap" | "tasks" | "liquidity" | "history";
export type MarketLpRewardLimit = { totalMicros: string | null; usedMicros: string; reservedMicros: string; remainingMicros: string | null };
export type MarketFundingSource = "reward" | "carats";
export type MarketCaratFunding = { availableCarats: string; estimatedReferenceMicros: string; capacityMicros: string;
  spendableMicros: string; surchargeBps: number; seasonId: string; endsAt: string; valuedAt: string };
export type MarketCaratDebit = { gameUnitAmount: string; seasonId: string; seasonAccountId: string;
  fundReferenceMicros: string; totalGameUnit: string; surchargeBps: number; valuedAt: string; endsAt: string };
export type MarketOverview = {
  userId: string; serverNow: string; network: "mainnet" | "testnet"; tokenSymbol: string;
  swapsEnabled: boolean; liquidityEnabled: boolean; executionEnabled: boolean;
  defaultSlippageBps: number; maxSlippageBps: number; minGramNano: string; minLpGramNano?: string; maxGramNano: string;
  lockSeconds: number; campaignId: string; rewardAvailableMicros: string; rewardReservedMicros: string;
  configVersionId?: string; rewardSpendableMicros?: string; lpRewardLimit?: MarketLpRewardLimit;
  caratFunding?: MarketCaratFunding | null;
  swapPending?: { limit: number; operations: { id: string; status: string; validUntil: string }[] };
  walletScope: "tapper";
  wallets: { address: string; addressUq: string | null; linkedAt: string }[]; masterAddress: string;
  contracts?: MarketContracts;
};
export type MarketTask = {
  id: string; code: string; title: MarketLocaleText; description: MarketLocaleText;
  direction: "buy" | "sell" | "caravan"; targetUsdtMicros: string; progressUsdtMicros: string;
  status: string; issuedAt: string; expiresAt: string; claimUntil: string; claimedAt: string | null;
  reward: { kind: "carats"; amount: string } | { kind: "balance-percent"; basisPoints: number; maximumAmount: string }
    | { kind: "passive-boost"; basisPoints: number; durationSeconds: number; fallbackCarats: string }
    | { kind: "boost-pack"; boosts: { kind: "tap-boost" | "passive-boost" | "energy-recovery-boost"; basisPoints: number; durationSeconds: number }[]; fallbackCarats: string };
  estimatedCarats: string;
  rewardReceipt?: MarketRewardReceipt | null;
  rewardAdjustments?: { carats: string; at: string }[];
};
export type MarketRewardReceipt = { assignmentId: string; carats: string; balanceSnapshot: string; claimedAt: string;
  boost: { startsAt: string; endsAt: string; multiplierBasisPoints: number } | null;
  boosts?: { kind: string; startsAt: string; endsAt: string; multiplierBasisPoints: number }[] };
export type MarketWalletRequest = {
  validUntil: number; from?: string; network?: "-239" | "-3";
  messages: { address: string; amount: string; payload?: string; stateInit?: string }[];
};
// Display-form addresses include flags/checksum, whereas TON Connect uses raw
// account IDs. This is identity comparison only; TON Proof is checked server-side.
// Format: https://docs.ton.org/foundations/addresses/formats
function marketWalletIdentity(value: string): { raw: string; testOnly: boolean } | null {
  const raw = /^(-?\d{1,3}):([a-fA-F0-9]{64})$/.exec(value);
  if (raw) {
    const workchain = Number(raw[1]);
    return workchain >= -128 && workchain <= 127 ? { raw: `${workchain}:${raw[2]?.toLowerCase()}`, testOnly: false } : null;
  }
  if (!/^[A-Za-z0-9_+/-]{48}$/.test(value)) return null;
  try {
    const bytes = Uint8Array.from(atob(value.replaceAll("-", "+").replaceAll("_", "/")), char => char.charCodeAt(0));
    const flags = bytes[0], workchainByte = bytes[1], checksumHigh = bytes[34], checksumLow = bytes[35];
    if (bytes.length !== 36 || flags === undefined || workchainByte === undefined || checksumHigh === undefined || checksumLow === undefined || ![0x11, 0x51].includes(flags & 0x7f)) return null;
    let checksum = 0;
    for (const byte of bytes.slice(0, 34)) {
      checksum ^= byte << 8;
      for (let bit = 0; bit < 8; bit++) checksum = ((checksum << 1) ^ ((checksum & 0x8000) ? 0x1021 : 0)) & 0xffff;
    }
    if (checksum !== (checksumHigh << 8 | checksumLow)) return null;
    const workchain = workchainByte > 127 ? workchainByte - 256 : workchainByte;
    return { raw: `${workchain}:${Array.from(bytes.slice(2, 34), byte => byte.toString(16).padStart(2, "0")).join("")}`, testOnly: Boolean(flags & 0x80) };
  } catch { return null; }
}
export function assertMarketWalletConnection(account: { address: string; chain: string } | null, expectedAddress: string, network: "mainnet" | "testnet"): string {
  const actual = marketWalletIdentity(account?.address ?? ""), expected = marketWalletIdentity(expectedAddress);
  if (!actual) throw new Error("market_connect_wallet");
  if (actual.raw !== expected?.raw) throw new Error("market_connect_wallet");
  if (account?.chain !== (network === "mainnet" ? "-239" : "-3") || (network === "mainnet" && (actual.testOnly || expected?.testOnly))) throw new Error("market_wallet_network");
  return actual.raw;
}
export type MarketConnectedWallet = { address: string; addressUq: string };
/** A saved DB address is not a live SDK connection. Reject legacy/shared APIs. */
export function resolveMarketWallet(overview: MarketOverview, account: { address: string; chain: string } | null): MarketConnectedWallet | null {
  if (overview.walletScope !== "tapper" || !account) return null;
  for (const binding of overview.wallets) {
    try {
      const address = assertMarketWalletConnection(account, binding.address, overview.network);
      const display = binding.addressUq;
      if (!display?.startsWith(overview.network === "mainnet" ? "U" : "0")) continue;
      assertMarketWalletConnection(account, display, overview.network);
      return { address, addressUq: display };
    } catch { /* Another address or network is never silently selected. */ }
  }
  return null;
}
export type MarketTrade = {
  id: string; direction: "buy" | "sell"; status: string; validUntil: string; createdAt: string;
  confirmedUsdtMicros: string | null; failureCode: string | null;
  quote: { offerAmount: string; expectedReceiveAmount: string; minReceiveAmount: string; slippagePercent: number; priceImpact: string;
    walletRequirements?: import("../../shared/marketSwapFunding").SwapWalletRequirements };
};
export type MarketLpQuote = {
  fundingSource?: MarketFundingSource; caratDebit?: MarketCaratDebit | null;
  sourceAvailableMicros?: string; sourceSpendableMicros?: string;
  availableCarats?: string | null;
  quoteId: string; tonPrincipalNano: string; requiredDiamondNano: string; executorFundingValueNano: string;
  estimatedReleasedNewNano: string; estimatedLpNano: string; accessUsdMicro: string;
  operationGasNano: string; protocolFeeNano: string; expiresAtMs: number;
  walletTransferValueNano?: string; walletExtraNano?: string; rewardAvailableMicros?: string;
  rewardSpendableMicros?: string; lpRewardLimit?: MarketLpRewardLimit;
  userContributionUsdtMicros?: string; platformContributionUsdtMicros?: string; estimatedPositionUsdtMicros?: string;
  controllerAddress?: string; lockerAddress?: string;
};
export type MarketContracts = { token: string; pool: string; controller: string; locker: string };
export type MarketToken = {
  symbol: string; network: "mainnet" | "testnet"; priceUsdtMicros: string | null; liquidityUsdtMicros: string | null;
  asOf: string | null; stale: boolean; points: { at: string; priceUsdtMicros: string }[]; contracts: MarketContracts;
};
export type MarketLpOperation = {
  id: string; status: string; publicStatus: string; createdAt: string; tonPrincipalNano: string;
  submittedTxHash?: string | null;
  canCancel?: boolean;
  beneficiaryAddress?: string;
  rewardReferenceMicros?: string; reservationStatus?: string;
  fundingSource?: MarketFundingSource; caratDebit?: string | null; fundingValuation?: MarketCaratDebit | null;
  walletRequest: { address: string; valueNano: string; payloadBoc: string; expiresAt: string } | null;
  lpSlot?: { id: string; lpAmountNano: string; unlockAt: string; status: string } | null;
};
export type MarketHistory = { trades: MarketTrade[]; liquidity: MarketLpOperation[] };

export type MarketPosition = {
  id: string; operationId: string; status: "LOCKED" | "UNLOCKED" | "WITHDRAW_PENDING" | "WITHDRAWN";
  lockedAt: string; unlockAt: string; withdrawnAt: string | null; lpAmountNano: string;
  tokenSymbol: string | null; beneficiaryAddress: string; beneficiaryAddressUq: string | null;
  gramContributionNano: string; rewardSpentMicros: string;
  fundingSource?: MarketFundingSource; caratDebit?: string | null;
  valueUsdtMicros: string | null; asOf: string | null; stale: boolean;
  gramAmountNano: string | null; tokenAmountNano: string | null;
  change24hMicros: string | null; change24hBps: string | null; changeFrom: string | null; changeTo: string | null;
  contracts: MarketContracts;
};
export type MarketPortfolio = {
  serverNow: string; network: "mainnet" | "testnet";
  summary: { activeCount: number; totalCount: number; valueUsdtMicros: string | null; unpricedCount: number;
    stale: boolean; asOf: string | null; change24hMicros: string | null; change24hBps: string | null;
    performanceIncomplete: boolean; performanceMethod: "ownership-interval-weighted"; changeFrom: string; changeTo: string };
  items: MarketPosition[]; nextCursor: string | null;
};

/** Never route financial values through floating point, including very large reward accounts. */
export function marketUnits(value: string, scale: number, locale: "ru" | "en", digits = 2): string {
  if (!/^\d+$/.test(value)) return "—";
  const amount = BigInt(value), unit = 10n ** BigInt(scale);
  const whole = (amount / unit).toLocaleString(locale === "ru" ? "ru-RU" : "en-US");
  const fraction = (amount % unit).toString().padStart(scale, "0").slice(0, Math.min(scale, digits));
  return fraction ? `${whole}${locale === "ru" ? "," : "."}${fraction}` : whole;
}
export function marketDecimalValid(value: string): boolean {
  return /^(0|[1-9]\d{0,17})([.,]\d{1,9})?$/.test(value) && /[1-9]/.test(value);
}
